import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Breadcrumb,
  Button,
  Dropdown,
  Layout,
  Menu,
  App,
  Select,
  Space,
  Tag,
  theme,
  Typography,
} from "antd";
import type { ItemType } from "antd/es/menu/interface";
import {
  ApartmentOutlined,
  BankOutlined,
  BookOutlined,
  CloudServerOutlined,
  CommentOutlined,
  DashboardOutlined,
  FileTextOutlined,
  IdcardOutlined,
  KeyOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  NotificationOutlined,
  SafetyCertificateOutlined,
  SettingOutlined,
  TeamOutlined,
  RocketOutlined,
  ThunderboltOutlined,
  UserOutlined,
  UserSwitchOutlined,
  AuditOutlined,
} from "../components/icons";
import { useSession, signOut } from "../lib/auth-client";
import { trpc } from "../trpc";
import { useScriptAssistantAccess } from "../hooks/useScriptAssistantAccess";
import { useTenantContext } from "../tenant/TenantContext";
import {
  canAccessAppPage,
  getDefaultLandingPage,
  type AppPageKey,
} from "../tenant/access";
import { getTrpcErrorMessage, tenantApi } from "../tenant/api";
import type { TenantRecord } from "../tenant/types";
import { UserManagement } from "./UserManagement";
import { TenantManagement } from "./TenantManagement";
import { SecurityCenter } from "./SecurityCenter";
import { ProfileSettings } from "./ProfileSettings";
import { SystemSettings } from "./SystemSettings";
import { NotificationModal } from "../components/NotificationModal";
import {
  ArchiveSourceSettingsPage,
  BroadcastStatsPage,
  BusinessUnitManagementPage,
  DispatchMonitorPage,
  MessageRecordsPage,
  RoomListPage,
  SpeakerRosterPage,
} from "./FadanDashboard";
import { GroupChatHistoryPage } from "./GroupChatHistoryPage";
import { ChurnMemberPage } from "./ChurnMemberPage";
import { SpeechPointManagement } from "./SpeechPointManagement";
import { AtmosphereMemberPage } from "./AtmosphereMemberPage";
import { AtmospherePersonaCategoryPage } from "./AtmospherePersonaCategoryPage";
import { AiProviderPage } from "./AiProviderPage";
import { AiModelPage } from "./AiModelPage";
import { ScriptSheetPage } from "./ScriptSheetPage";
import { ScriptSheetSimplePage } from "./ScriptSheetSimplePage";
import { DispatchScriptParsePage } from "./DispatchScriptParsePage";
import { DispatchScriptBindPage } from "./DispatchScriptBindPage";
import { DispatchScriptProgressPage } from "./DispatchScriptProgressPage";
import { DispatchScriptMaterialPage } from "./DispatchScriptMaterialPage";
import { ScriptRoleReviewRecordsPage } from "./ScriptRoleReviewRecordsPage";
import logo from "../assets/logo.svg";

interface Profile {
  nickname: string;
  avatar: string;
}

const { Header, Sider, Content } = Layout;
const { Text } = Typography;

const pageTitles: Record<string, string> = {
  monitor: "发单监控看板",
  businessUnits: "业务单元管理",
  messages: "消息记录",
  groupChat: "群聊天记录",
  roomList: "群列表",
  speakerRoster: "发言名单",
  churnMembers: "退群成员",
  speechPoints: "发言积分",
  stats: "发单统计",
  dispatchScriptParse: "剧本解析",
  dispatchScriptBind: "剧本绑定",
  dispatchScriptProgress: "发单情况",
  dispatchScriptMaterial: "素材列表",
  sources: "会话存档源",
  users: "用户与权限",
  tenants: "租户管理",
  atmosphereProfile: "气氛员画像",
  atmospherePersonaCategory: "人设类目",
  scriptSheet: "开始匹配",
  scriptRoleReviewRecords: "审查记录",
  aiProvider: "供应商管理",
  aiModel: "模型管理",
  settings: "系统设置",
  security: "安全中心",
  profile: "个人资料",
};

export function HomePage() {
  const { data: session } = useSession();
  const { message } = App.useApp();
  const {
    context,
    isAdmin,
    isPlatformSuperAdmin,
    isAwayFromHome,
    activeTenantLabel,
    homeTenantLabel,
    roleText,
    switchTenant,
  } = useTenantContext();
  const {
    access: scriptAccess,
    loading: scriptAccessLoading,
    error: scriptAccessError,
  } = useScriptAssistantAccess(Boolean(context?.features.atmosphere_assistant));
  const [collapsed, setCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(() => {
    const stored = localStorage.getItem("currentPage");
    return stored && pageTitles[stored] ? stored : "monitor";
  });
  const [profile, setProfile] = useState<Profile | null>(null);
  const [tenantOptions, setTenantOptions] = useState<TenantRecord[]>([]);
  const [tenantOptionsLoading, setTenantOptionsLoading] = useState(false);
  const [switchingTenant, setSwitchingTenant] = useState(false);
  const {
    token: { colorBgContainer },
  } = theme.useToken();

  const features = context?.features;
  const pageAccessBase = useMemo(
    () => ({
      role: context?.role,
      isPlatformSuperAdmin,
      features: features || {
        dispatch_monitor: false,
        script_monitor: false,
        atmosphere_assistant: false,
        speech_points: false,
        ai_management: false,
      },
      scriptAccess: {
        atmosphereGroups: scriptAccess.atmosphereGroups,
        personaCategoryEnabled: scriptAccess.personaCategoryEnabled,
        feishuScriptEnabled: scriptAccess.feishuScriptEnabled,
      },
    }),
    [context?.role, isPlatformSuperAdmin, features, scriptAccess]
  );

  const canAccessPage = useMemo(() => {
    return (page: string) =>
      canAccessAppPage({
        ...pageAccessBase,
        page: page as AppPageKey,
      });
  }, [pageAccessBase]);

  const canAccessAtmosphereProfile = canAccessPage("atmosphereProfile");
  const canAccessPersonaCategory = canAccessPage("atmospherePersonaCategory");
  const canAccessScriptSheet = canAccessPage("scriptSheet");
  const hasScriptAssistantMenu =
    canAccessAtmosphereProfile || canAccessPersonaCategory || canAccessScriptSheet || canAccessPage("scriptRoleReviewRecords");
  const canAccessDispatchMonitor = canAccessPage("monitor");
  const canAccessScriptMonitor =
    canAccessPage("dispatchScriptParse") ||
    canAccessPage("dispatchScriptBind") ||
    canAccessPage("dispatchScriptProgress") ||
    canAccessPage("dispatchScriptMaterial");
  const canAccessSpeechPoints = canAccessPage("speechPoints");
  const canAccessAi =
    canAccessPage("aiProvider") || canAccessPage("aiModel");
  const canAccessUsers = canAccessPage("users");
  const canAccessTenants = canAccessPage("tenants");
  const canAccessChurnMembers = canAccessPage("churnMembers");

  useEffect(() => {
    const query = window.matchMedia("(max-width: 768px)");
    const syncMobileState = () => {
      setIsMobile(query.matches);
      if (!query.matches) setMobileMenuOpen(false);
    };

    syncMobileState();
    query.addEventListener("change", syncMobileState);
    return () => query.removeEventListener("change", syncMobileState);
  }, []);

  useEffect(() => {
    if (!session) return;
    trpc.profile.get.query().then(setProfile).catch(console.error);
  }, [session]);

  useEffect(() => {
    if (scriptAccessError) {
      message.error(scriptAccessError);
    }
  }, [message, scriptAccessError]);

  useEffect(() => {
    localStorage.setItem("currentPage", currentPage);
  }, [currentPage]);

  useEffect(() => {
    if (!isPlatformSuperAdmin) {
      setTenantOptions([]);
      return;
    }
    let cancelled = false;
    setTenantOptionsLoading(true);
    tenantApi
      .list()
      .then((rows) => {
        if (!cancelled) setTenantOptions(rows);
      })
      .catch((err) => {
        if (!cancelled) {
          message.error(getTrpcErrorMessage(err, "加载可切换租户失败"));
        }
      })
      .finally(() => {
        if (!cancelled) setTenantOptionsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isPlatformSuperAdmin, message]);

  useEffect(() => {
    if (scriptAccessLoading || !context) return;

    if (!canAccessPage(currentPage)) {
      setCurrentPage(getDefaultLandingPage(pageAccessBase));
      return;
    }

    if (
      currentPage === "dashboard" ||
      currentPage === "announcements" ||
      currentPage === "content"
    ) {
      setCurrentPage(getDefaultLandingPage(pageAccessBase));
    }
  }, [currentPage, scriptAccessLoading, canAccessPage, context, pageAccessBase]);

  const handleLogout = async () => {
    await signOut();
    message.success("已安全退出");
  };

  const handleMenuClick = (key: string) => {
    setCurrentPage(key);
    if (isMobile) setMobileMenuOpen(false);
  };

  const handleSwitchTenant = async (tenantId: string) => {
    if (!tenantId || tenantId === context?.activeTenant.id) return;
    setSwitchingTenant(true);
    try {
      await switchTenant(tenantId);
      message.success("已切换租户");
    } catch (err) {
      message.error(getTrpcErrorMessage(err, "切换租户失败"));
      setSwitchingTenant(false);
    }
  };

  const scriptAssistantChildren: ItemType[] = [];
  if (canAccessPersonaCategory) {
    scriptAssistantChildren.push({
      key: "atmospherePersonaCategory",
      icon: <UserSwitchOutlined />,
      label: "人设类目",
    });
  }
  if (canAccessAtmosphereProfile) {
    scriptAssistantChildren.push({
      key: "atmosphereProfile",
      icon: <IdcardOutlined />,
      label: "气氛员画像",
    });
  }
  if (canAccessScriptSheet) {
    scriptAssistantChildren.push({
      key: "scriptSheet",
      icon: <FileTextOutlined />,
      label: "开始匹配",
    });
  }
  if (canAccessPage("scriptRoleReviewRecords")) {
    scriptAssistantChildren.push({
      key: "scriptRoleReviewRecords",
      icon: <AuditOutlined />,
      label: "审查记录",
    });
  }

  const menuItems: ItemType[] = [];

  if (canAccessDispatchMonitor) {
    menuItems.push({
      key: "monitor",
      icon: <DashboardOutlined />,
      label: "发单监控",
    });
  }

  if (isAdmin && canAccessDispatchMonitor) {
    if (canAccessPage("sources")) {
      menuItems.push({
        key: "sources",
        icon: <KeyOutlined />,
        label: "存档源配置",
      });
    }
    if (canAccessPage("roomList")) {
      menuItems.push({
        key: "roomList",
        icon: <TeamOutlined />,
        label: "群列表",
      });
    }
    if (canAccessPage("businessUnits")) {
      menuItems.push({
        key: "businessUnits",
        icon: <TeamOutlined />,
        label: "业务单元",
      });
    }
    if (canAccessPage("stats")) {
      menuItems.push({
        key: "stats",
        icon: <FileTextOutlined />,
        label: "发单统计",
      });
    }
  }

  if (canAccessScriptMonitor) {
    const children: ItemType[] = [];
    if (canAccessPage("dispatchScriptParse")) {
      children.push({
        key: "dispatchScriptParse",
        icon: <FileTextOutlined />,
        label: "剧本解析",
      });
    }
    if (canAccessPage("dispatchScriptBind")) {
      children.push({
        key: "dispatchScriptBind",
        icon: <KeyOutlined />,
        label: "剧本绑定",
      });
    }
    if (canAccessPage("dispatchScriptProgress")) {
      children.push({
        key: "dispatchScriptProgress",
        icon: <DashboardOutlined />,
        label: "发单情况",
      });
    }
    if (canAccessPage("dispatchScriptMaterial")) {
      children.push({
        key: "dispatchScriptMaterial",
        icon: <FileTextOutlined />,
        label: "素材列表",
      });
    }
    if (children.length > 0) {
      menuItems.push({
        key: "dispatchScriptMonitor",
        icon: <BookOutlined />,
        label: "剧本监测",
        children,
      });
    }
  }

  if (isAdmin && canAccessDispatchMonitor) {
    if (canAccessPage("messages")) {
      menuItems.push({
        key: "messages",
        icon: <NotificationOutlined />,
        label: "消息记录",
      });
    }
    if (canAccessPage("groupChat")) {
      menuItems.push({
        key: "groupChat",
        icon: <CommentOutlined />,
        label: "群聊天记录",
      });
    }
    if (canAccessPage("speakerRoster")) {
      menuItems.push({
        key: "speakerRoster",
        icon: <SafetyCertificateOutlined />,
        label: "发言名单",
      });
    }
    if (canAccessPage("churnMembers")) {
      menuItems.push({
        key: "churnMembers",
        icon: <LogoutOutlined />,
        label: "退群成员",
      });
    }
  }

  if (canAccessSpeechPoints && canAccessPage("speechPoints")) {
    menuItems.push({
      key: "speechPoints",
      icon: <ThunderboltOutlined />,
      label: "发言积分",
    });
  }

  if (canAccessUsers) {
    menuItems.push({
      key: "users",
      icon: <TeamOutlined />,
      label: "用户与权限",
    });
  }

  if (canAccessTenants) {
    menuItems.push({
      key: "tenants",
      icon: <BankOutlined />,
      label: "租户管理",
    });
  }

  if (hasScriptAssistantMenu && scriptAssistantChildren.length > 0) {
    menuItems.push({
      key: "scriptAssistant",
      icon: <BookOutlined />,
      label: "气氛员助手",
      children: scriptAssistantChildren,
    });
  }

  if (canAccessAi) {
    const children: ItemType[] = [];
    if (canAccessPage("aiProvider")) {
      children.push({
        key: "aiProvider",
        icon: <KeyOutlined />,
        label: "供应商管理",
      });
    }
    if (canAccessPage("aiModel")) {
      children.push({
        key: "aiModel",
        icon: <RocketOutlined />,
        label: "模型管理",
      });
    }
    if (children.length > 0) {
      menuItems.push({
        key: "aiVendor",
        icon: <CloudServerOutlined />,
        label: "AI 供应商",
        children,
      });
    }
  }

  menuItems.push(
    {
      key: "settings",
      icon: <SettingOutlined />,
      label: "系统设置",
    },
    {
      key: "security",
      icon: <SafetyCertificateOutlined />,
      label: "安全中心",
    }
  );

  const userMenu = {
    items: [
      {
        key: "profile",
        label: "个人资料",
        icon: <UserOutlined />,
        onClick: () => setCurrentPage("profile"),
      },
      {
        key: "settings",
        label: "偏好设置",
        icon: <SettingOutlined />,
        onClick: () => setCurrentPage("settings"),
      },
      { type: "divider" as const },
      {
        key: "logout",
        label: "退出登录",
        icon: <LogoutOutlined />,
        danger: true,
        onClick: handleLogout,
      },
    ],
  };

  const siderWidth = 240;
  const collapsedWidth = 80;
  const mainMarginLeft = isMobile ? 0 : collapsed ? collapsedWidth : siderWidth;
  const navigationIcon = isMobile
    ? mobileMenuOpen
      ? <MenuFoldOutlined />
      : <MenuUnfoldOutlined />
    : collapsed
      ? <MenuUnfoldOutlined />
      : <MenuFoldOutlined />;

  const selectOptions = tenantOptions.map((tenant) => ({
    value: tenant.id,
    label: `${tenant.name}${tenant.code ? ` (${tenant.code})` : ""}${
      String(tenant.status).toLowerCase() !== "active" ? " · 停用" : ""
    }`,
  }));

  return (
    <Layout style={{ minHeight: "100vh" }}>
      {isMobile && mobileMenuOpen && (
        <div
          className="home-sider-mask"
          onClick={() => setMobileMenuOpen(false)}
          aria-hidden="true"
        />
      )}

      <Sider
        trigger={null}
        collapsible
        collapsed={!isMobile && collapsed}
        collapsedWidth={collapsedWidth}
        className="home-sider"
        style={{
          background: "#fff",
          borderRight: "1px solid rgba(15, 23, 42, 0.08)",
          position: "fixed",
          left: 0,
          top: 0,
          bottom: 0,
          zIndex: 102,
          transform: isMobile && !mobileMenuOpen ? "translateX(-100%)" : "translateX(0)",
          transition: "transform 0.22s ease, width 0.2s",
          boxShadow:
            isMobile && mobileMenuOpen ? "0 24px 64px rgba(15, 23, 42, 0.22)" : undefined,
        }}
        width={siderWidth}
      >
        <div
          style={{
            height: 64,
            display: "flex",
            alignItems: "center",
            justifyContent: !isMobile && collapsed ? "center" : "flex-start",
            padding: !isMobile && collapsed ? 0 : "0 24px",
            borderBottom: "1px solid rgba(15, 23, 42, 0.08)",
          }}
        >
          <img src={logo} alt="发单监控系统" style={{ width: 32, height: 32 }} />
          {(isMobile || !collapsed) && (
            <span
              style={{
                marginLeft: 12,
                fontSize: 18,
                fontWeight: 700,
                color: "#1e293b",
                whiteSpace: "nowrap",
              }}
            >
              发单监控系统
            </span>
          )}
        </div>

        <div className="home-sider-menu-scroll">
          <Menu
            mode="inline"
            selectedKeys={[currentPage]}
            items={menuItems}
            style={{ borderRight: 0, padding: "8px 0" }}
            onClick={({ key }) => handleMenuClick(String(key))}
          />
        </div>
      </Sider>

      <Layout
        style={{
          marginLeft: mainMarginLeft,
          transition: "margin-left 0.2s",
          minWidth: 0,
        }}
      >
        <Header
          className="home-header"
          style={{
            padding: 0,
            background: colorBgContainer,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            position: "sticky",
            top: 0,
            zIndex: 100,
            boxShadow: "0 1px 2px 0 rgba(15, 23, 42, 0.04)",
          }}
        >
          <div
            className="home-header-main"
            style={{ display: "flex", alignItems: "center", minWidth: 0 }}
          >
            <Button
              type="text"
              icon={navigationIcon}
              onClick={() => {
                if (isMobile) {
                  setMobileMenuOpen((open) => !open);
                } else {
                  setCollapsed((value) => !value);
                }
              }}
              style={{ fontSize: "16px", width: 64, height: 64 }}
              aria-label={isMobile ? "打开导航" : "折叠导航"}
            />
            <Breadcrumb
              className="home-breadcrumb"
              items={[
                { title: "首页" },
                { title: pageTitles[currentPage] || "发单监控" },
              ]}
              style={{ marginLeft: 8 }}
            />
          </div>

          <div
            className="home-header-actions"
            style={{
              display: "flex",
              alignItems: "center",
              paddingRight: 24,
              gap: 12,
              minWidth: 0,
            }}
          >
            <div className="home-tenant-switch">
              <Space size={8} wrap>
                <ApartmentOutlined style={{ color: "#0f766e" }} />
                <Text type="secondary" className="home-tenant-label">
                  当前租户
                </Text>
                {isPlatformSuperAdmin ? (
                  <Select
                    size="small"
                    style={{ minWidth: 160, maxWidth: 240 }}
                    loading={tenantOptionsLoading || switchingTenant}
                    value={context?.activeTenant.id}
                    options={
                      selectOptions.length > 0
                        ? selectOptions
                        : context?.activeTenant
                          ? [
                              {
                                value: context.activeTenant.id,
                                label: activeTenantLabel,
                              },
                            ]
                          : []
                    }
                    onChange={(value) => void handleSwitchTenant(String(value))}
                    placeholder="切换租户"
                  />
                ) : (
                  <Tag color="processing" style={{ marginInlineEnd: 0 }}>
                    {activeTenantLabel}
                  </Tag>
                )}
              </Space>
            </div>
            <NotificationModal />
            <Dropdown menu={userMenu} placement="bottomRight" arrow>
              <div
                className="home-user-trigger"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  cursor: "pointer",
                  padding: "4px 8px",
                  borderRadius: 6,
                }}
              >
                <Avatar
                  style={{
                    backgroundColor: profile?.avatar ? "transparent" : "#6366f1",
                  }}
                  icon={<UserOutlined />}
                  src={profile?.avatar || undefined}
                />
                <span
                  className="home-user-name"
                  style={{ fontWeight: 500, color: "#334155" }}
                >
                  {profile?.nickname ||
                    session?.user?.name ||
                    session?.user?.email?.split("@")[0]}
                </span>
              </div>
            </Dropdown>
          </div>
        </Header>

        {isAwayFromHome && (
          <div className="home-tenant-away-banner">
            <Alert
              type="warning"
              showIcon
              banner
              message={
                <span>
                  正在操作租户 <Text strong>{activeTenantLabel}</Text>
                  ，你的归属租户是 <Text strong>{homeTenantLabel}</Text>
                  。角色：{roleText}
                </span>
              }
            />
          </div>
        )}

        <Content
          className="home-content"
          style={{
            margin: isMobile ? "16px 12px" : "24px 16px",
            padding: isMobile ? 0 : 24,
            minHeight: 280,
          }}
        >
          {currentPage === "monitor" && canAccessPage("monitor") && <DispatchMonitorPage />}
          {currentPage === "sources" && canAccessPage("sources") && <ArchiveSourceSettingsPage />}
          {currentPage === "roomList" && canAccessPage("roomList") && <RoomListPage />}
          {currentPage === "businessUnits" && canAccessPage("businessUnits") && (
            <BusinessUnitManagementPage />
          )}
          {currentPage === "stats" && canAccessPage("stats") && <BroadcastStatsPage />}
          {currentPage === "dispatchScriptParse" && canAccessPage("dispatchScriptParse") && (
            <DispatchScriptParsePage />
          )}
          {currentPage === "dispatchScriptBind" && canAccessPage("dispatchScriptBind") && (
            <DispatchScriptBindPage />
          )}
          {currentPage === "dispatchScriptProgress" &&
            canAccessPage("dispatchScriptProgress") && <DispatchScriptProgressPage />}
          {currentPage === "dispatchScriptMaterial" &&
            canAccessPage("dispatchScriptMaterial") && <DispatchScriptMaterialPage />}
          {currentPage === "messages" && canAccessPage("messages") && <MessageRecordsPage />}
          {currentPage === "groupChat" && canAccessPage("groupChat") && <GroupChatHistoryPage />}
          {currentPage === "speakerRoster" && canAccessPage("speakerRoster") && (
            <SpeakerRosterPage />
          )}
          {currentPage === "churnMembers" && canAccessPage("churnMembers") && <ChurnMemberPage />}
          {currentPage === "speechPoints" && canAccessPage("speechPoints") && (
            <SpeechPointManagement />
          )}
          {currentPage === "users" && canAccessPage("users") && <UserManagement />}
          {currentPage === "tenants" && canAccessPage("tenants") && <TenantManagement />}
          {currentPage === "atmosphereProfile" && canAccessAtmosphereProfile && (
            <AtmosphereMemberPage />
          )}
          {currentPage === "atmospherePersonaCategory" && canAccessPersonaCategory && (
            <AtmospherePersonaCategoryPage />
          )}
          {currentPage === "scriptSheet" && canAccessScriptSheet && (
            isAdmin ? <ScriptSheetPage /> : <ScriptSheetSimplePage />
          )}
          {currentPage === "scriptRoleReviewRecords" &&
            canAccessPage("scriptRoleReviewRecords") && <ScriptRoleReviewRecordsPage />}
          {currentPage === "aiProvider" && canAccessPage("aiProvider") && <AiProviderPage />}
          {currentPage === "aiModel" && canAccessPage("aiModel") && <AiModelPage />}
          {currentPage === "settings" && <SystemSettings />}
          {currentPage === "security" && <SecurityCenter />}
          {currentPage === "profile" && <ProfileSettings />}
        </Content>
      </Layout>
    </Layout>
  );
}
